import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  BookOpen,
  FileText,
  Code,
  Lightbulb,
  Zap,
  Settings,
  Users,
  Shield,
  CreditCard,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/ui/logo";

const gettingStartedSteps = [
  {
    step: "1",
    title: "Create an account",
    description:
      "Sign up with your email and verify your address. You'll get free monthly credits to start reviewing immediately.",
  },
  {
    step: "2",
    title: "Create a project",
    description:
      "Organize your work by creating a project. Projects help you group related requirements and track improvements over time.",
  },
  {
    step: "3",
    title: "Submit requirements",
    description:
      "Paste your requirements text — user stories, feature specs, PRDs, or acceptance criteria — into the review wizard.",
  },
  {
    step: "4",
    title: "Configure your review",
    description:
      "Choose your review mode (Quick, Standard, or Deep), select personas to simulate stakeholder perspectives, and set your preferences.",
  },
  {
    step: "5",
    title: "Review results",
    description:
      "Get a detailed clarity score, flagged issues, rewritten suggestions, and clarifying questions — all in seconds.",
  },
];

const reviewModes = [
  {
    mode: "Quick",
    credits: "1 credit",
    description:
      "Fast surface-level check for obvious ambiguities, missing acceptance criteria, and vague language. Best for initial drafts.",
    time: "~10 seconds",
  },
  {
    mode: "Standard",
    credits: "3 credits",
    description:
      "Comprehensive review including dependency analysis, assumption detection, edge case identification, and rewritten suggestions.",
    time: "~30 seconds",
  },
  {
    mode: "Deep",
    credits: "5 credits",
    description:
      "Full analysis with persona-based feedback, cross-requirement consistency checks, risk assessment, and prioritised recommendations.",
    time: "~60 seconds",
  },
];

const clarityScoreBreakdown = [
  {
    range: "90–100",
    label: "Excellent",
    color: "text-green-600 dark:text-green-400",
    description: "Ready for engineering. Clear, unambiguous, with explicit acceptance criteria.",
  },
  {
    range: "70–89",
    label: "Good",
    color: "text-blue-600 dark:text-blue-400",
    description: "Minor improvements recommended. A few assumptions or edge cases to address.",
  },
  {
    range: "50–69",
    label: "Needs Work",
    color: "text-yellow-600 dark:text-yellow-400",
    description: "Significant ambiguities detected. Multiple rewrites and clarifications suggested.",
  },
  {
    range: "0–49",
    label: "Critical",
    color: "text-red-600 dark:text-red-400",
    description: "High risk of misinterpretation. Major restructuring recommended before sharing with engineering.",
  },
];

const apiEndpoints = [
  { method: "POST", path: "/api/v1/reviews", description: "Submit requirements for review" },
  { method: "GET", path: "/api/v1/reviews/:id", description: "Retrieve review results" },
  { method: "GET", path: "/api/v1/projects", description: "List all projects" },
  { method: "POST", path: "/api/v1/projects", description: "Create a new project" },
  { method: "GET", path: "/api/v1/personas", description: "List available personas" },
  { method: "GET", path: "/api/v1/credits/balance", description: "Check credit balance" },
];

export default function Docs() {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Documentation"
        description="Complete documentation for Reqqy — learn how to review requirements, use personas, understand clarity scores, and integrate via API."
        canonical="/docs"
      />

      {/* Header */}
      <header className="border-b border-border">
        <div className="container flex items-center justify-between h-16 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo variant="full" className="h-8 w-auto" />
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to="/home">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="container px-4 md:px-6 py-12 md:py-16 max-w-4xl">
        <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">Documentation</h1>
        <p className="text-lg text-muted-foreground mb-12">
          Everything you need to know about using Reqqy to review, improve, and ship clearer requirements.
        </p>

        {/* Table of Contents */}
        <nav className="p-6 rounded-xl border border-border bg-muted/30 mb-12">
          <h2 className="text-lg font-semibold mb-4">Contents</h2>
          <ul className="grid gap-2 md:grid-cols-2">
            {[
              { href: "#getting-started", label: "Getting Started", icon: BookOpen },
              { href: "#review-modes", label: "Review Modes", icon: Zap },
              { href: "#clarity-scores", label: "Clarity Scores", icon: CheckCircle2 },
              { href: "#personas", label: "Personas", icon: Users },
              { href: "#projects", label: "Projects & Runs", icon: FileText },
              { href: "#credits", label: "Credits & Billing", icon: CreditCard },
              { href: "#api", label: "API Reference", icon: Code },
              { href: "#best-practices", label: "Best Practices", icon: Lightbulb },
            ].map(({ href, label, icon: Icon }) => (
              <li key={href}>
                <a
                  href={href}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Getting Started */}
        <section id="getting-started" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold">Getting Started</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Get up and running with Reqqy in under five minutes. Follow these steps to submit your first requirements
            review.
          </p>
          <div className="space-y-4">
            {gettingStartedSteps.map(({ step, title, description }) => (
              <div key={step} className="flex gap-4 p-4 rounded-lg border border-border bg-card">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">{step}</span>
                </div>
                <div>
                  <h3 className="font-medium mb-1">{title}</h3>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Review Modes */}
        <section id="review-modes" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold">Review Modes</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Choose the depth of analysis that matches your needs. Each mode consumes a different number of credits.
          </p>
          <div className="grid gap-4">
            {reviewModes.map(({ mode, credits, description, time }) => (
              <div key={mode} className="p-5 rounded-xl border border-border bg-card">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">{mode}</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">{time}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                      {credits}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Clarity Scores */}
        <section id="clarity-scores" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold">Clarity Scores</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Every review produces a clarity score between 0 and 100. This score reflects how clear, complete, and
            unambiguous your requirements are based on multiple weighted dimensions.
          </p>
          <div className="space-y-3">
            {clarityScoreBreakdown.map(({ range, label, color, description }) => (
              <div key={range} className="flex items-start gap-4 p-4 rounded-lg border border-border bg-card">
                <div className="text-center shrink-0 w-16">
                  <span className={`text-lg font-bold ${color}`}>{range}</span>
                </div>
                <div>
                  <h3 className="font-medium mb-1">{label}</h3>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border">
            <h4 className="font-medium mb-2">Score Dimensions</h4>
            <ul className="grid gap-1 md:grid-cols-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" /> Specificity & measurability
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" /> Completeness of acceptance criteria
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" /> Absence of ambiguous language
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" /> Dependency clarity
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" /> Testability of requirements
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" /> Consistency across requirements
              </li>
            </ul>
          </div>
        </section>

        {/* Personas */}
        <section id="personas" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold">Personas</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Personas simulate different stakeholder perspectives when reviewing your requirements. They help surface
            concerns that might be missed by a single viewpoint.
          </p>
          <div className="space-y-4">
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Built-in Personas</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Reqqy includes pre-configured personas representing common stakeholder roles:
              </p>
              <ul className="grid gap-2 md:grid-cols-2 text-sm text-muted-foreground">
                <li>
                  • <strong className="text-foreground">Senior Engineer</strong> — Focuses on technical feasibility and
                  edge cases
                </li>
                <li>
                  • <strong className="text-foreground">QA Lead</strong> — Evaluates testability and acceptance criteria
                </li>
                <li>
                  • <strong className="text-foreground">Product Manager</strong> — Assesses business value and user
                  impact
                </li>
                <li>
                  • <strong className="text-foreground">UX Designer</strong> — Reviews usability and interaction clarity
                </li>
              </ul>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Custom Personas</h3>
              <p className="text-sm text-muted-foreground">
                Create your own personas with specific roles, goals, and constraints. Custom personas can be private,
                shared with your project team, or across your entire organisation. You can also grant individual users
                viewer or editor permissions for fine-grained access control.
              </p>
            </div>
          </div>
        </section>

        {/* Projects & Runs */}
        <section id="projects" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold">Projects & Runs</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Organise your requirements reviews into projects and track improvements over time.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Projects</h3>
              <p className="text-sm text-muted-foreground">
                A project groups related requirements together. Each project has its own set of runs, assigned personas,
                and analytics. Projects can belong to your personal workspace or an organisation.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Runs</h3>
              <p className="text-sm text-muted-foreground">
                A run is a single review execution. Each run captures the input requirements, review mode, selected
                personas, clarity score, flagged issues, and improvement suggestions. Runs are immutable once completed.
              </p>
            </div>
          </div>
        </section>

        {/* Credits & Billing */}
        <section id="credits" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold">Credits & Billing</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Reqqy uses a credit-based system. Your subscription plan includes a monthly credit allocation that resets
            each billing cycle.
          </p>
          <div className="space-y-4">
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Monthly Credits</h3>
              <p className="text-sm text-muted-foreground">
                Each plan includes a set number of monthly credits. Unused monthly credits do not roll over. Your
                balance resets at the start of each billing cycle.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Top-up Credits</h3>
              <p className="text-sm text-muted-foreground">
                Need more credits? Purchase top-up packs that are added to your balance immediately. Top-up credits are
                used after your monthly allocation and expire after 12 months.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Credit Consumption</h3>
              <p className="text-sm text-muted-foreground">
                Credits are consumed based on the review mode selected: Quick (1 credit), Standard (3 credits), Deep (5
                credits). The credit estimator in the review wizard shows exactly how many credits a run will cost
                before you submit.
              </p>
            </div>
          </div>
        </section>

        {/* API Reference */}
        <section id="api" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Code className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold">API Reference</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Integrate Reqqy into your CI/CD pipeline, project management tools, or custom workflows with our REST API.
          </p>
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="bg-muted/50 px-5 py-3 border-b border-border">
              <h3 className="font-semibold text-sm">Endpoints</h3>
            </div>
            <div className="divide-y divide-border">
              {apiEndpoints.map(({ method, path, description }) => (
                <div key={path} className="flex items-center gap-4 px-5 py-3">
                  <span
                    className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${method === "POST" ? "bg-green-500/10 text-green-600 dark:text-green-400" : "bg-blue-500/10 text-blue-600 dark:text-blue-400"}`}
                  >
                    {method}
                  </span>
                  <code className="text-sm font-mono text-foreground">{path}</code>
                  <span className="text-sm text-muted-foreground ml-auto hidden md:block">{description}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 p-4 rounded-lg bg-muted/50 border border-border">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Authentication:</strong> All API requests require a Bearer token in
              the Authorization header. Generate API keys from your{" "}
              <Link to="/settings" className="text-primary hover:underline">
                Settings
              </Link>{" "}
              page.
            </p>
          </div>
        </section>

        {/* Best Practices */}
        <section id="best-practices" className="mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold">Best Practices</h2>
          </div>
          <div className="space-y-4">
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Write one requirement per review</h3>
              <p className="text-sm text-muted-foreground">
                While Reqqy can handle multiple requirements in a single run, you'll get the most detailed feedback by
                submitting one requirement at a time. This allows for deeper analysis of dependencies and edge cases.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Include context</h3>
              <p className="text-sm text-muted-foreground">
                Provide background information about the feature, target users, and system constraints. Context helps
                Reqqy generate more relevant clarifying questions and identify domain-specific ambiguities.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Use personas for cross-functional feedback</h3>
              <p className="text-sm text-muted-foreground">
                Select multiple personas to simulate a real requirements review meeting. Different perspectives catch
                different issues — a QA lead spots untestable criteria while a UX designer flags unclear user flows.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Iterate on your clarity score</h3>
              <p className="text-sm text-muted-foreground">
                Don't stop at the first review. Apply the suggested rewrites, address the clarifying questions, and run
                another review. Most teams see scores improve from 40–60 to 85+ after two iterations.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Review before sprint planning</h3>
              <p className="text-sm text-muted-foreground">
                Run requirements through Reqqy before they enter your backlog. This prevents ambiguous stories from
                reaching engineers and reduces the back-and-forth during sprint planning.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="p-8 rounded-xl border border-border bg-muted/50 text-center">
          <h2 className="text-xl font-semibold mb-2">Need more help?</h2>
          <p className="text-muted-foreground mb-4">Our team is here to help you get the most out of Reqqy.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild>
              <a href="mailto:support@reqqy.app">Contact Support</a>
            </Button>
            <Button asChild variant="outline">
              <Link to="/contact-sales">
                Talk to Sales
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
